import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { CirclePause, ScanBarcode } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchCatalog, fetchCategories, searchProducts, setSelectedCategory, setSearchQuery, clearSearch, lookupBarcode } from './catalogSlice';
import { BarcodeScannerModal, hasBarcodeDetector } from './BarcodeScannerModal';
import { VariantPickerModal } from './VariantPickerModal';
import { ProductTile } from './ProductTile';
import { addItem, setHeldCartCount, updateQuantity } from '@/features/cart/cartSlice';
import { CartPanel } from '@/features/cart/CartPanel';
import { FloatingCartBar } from '@/features/cart/FloatingCartBar';
import { useCartController } from '@/features/cart/useCartController';
import { StoreHeader } from '@/components/layout/StoreHeader';
import { Alert, Chip, IconButton, LoadingSkeleton, SearchPill } from '@/components/ui';
import { Product } from '@/services/types';
import api, { getErrorMessage } from '@/services/api';
import { API_ENDPOINTS } from '@/utils/constants';
import { useIsLandscape } from '@/hooks/useMediaQuery';

/** On phones nothing else owns the cart recalculation, so the catalog drives it. */
const CartSync: React.FC = () => { useCartController(); return null; };

/**
 * 04 Katalog — the selling screen. Landscape: catalog + pinned CartPanel. Phone: catalog + floating cart bar.
 */
const CatalogPage: React.FC = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const isLandscape = useIsLandscape();
    const { selectedShop } = useAppSelector((state) => state.auth);
    const { products, categories, selectedCategory, searchQuery, searchResults, isLoading, error } = useAppSelector((state) => state.catalog);
    const cartItems = useAppSelector((state) => state.cart.items);
    const heldCartCount = useAppSelector((state) => state.cart.heldCartCount) || 0;

    const [searchInput, setSearchInput] = useState('');
    const [showScanner, setShowScanner] = useState(false);
    const [variantParent, setVariantParent] = useState<Product | null>(null);
    const [lastScanned, setLastScanned] = useState<Product | null>(null);

    const qtyByComposite = useMemo(() => {
        const m = new Map<number, number>();
        cartItems.forEach((i) => m.set(i.compositeId, i.quantity));
        return m;
    }, [cartItems]);
    const qtyFor = (p: Product) => p.variants?.length
        ? p.variants.reduce((n, v) => n + (qtyByComposite.get(v.id) ?? 0), 0)
        : qtyByComposite.get(p.id) ?? 0;

    // A parent with variants is never sold itself (docs/POS_VARIANT_API.md): open the picker instead.
    const handleAddToCart = React.useCallback((product: Product, qty = 1) => {
        if (product.variants?.length) {
            setVariantParent(product);
            return;
        }
        dispatch(addItem(product));
        if (qty > 1) dispatch(updateQuantity({ compositeId: product.id, quantity: (qtyByComposite.get(product.id) ?? 0) + qty }));
        toast.success(t('catalog.addedToCart', { name: product.name, defaultValue: `Added ${product.name} to cart` }));
    }, [dispatch, t, qtyByComposite]);

    // Barcode → product → cart. Used by keyboard-wedge scanners (Enter) and the camera scanner.
    const handleBarcode = React.useCallback(async (barcode: string) => {
        if (!selectedShop) return;
        try {
            const product = await dispatch(lookupBarcode({ shopId: selectedShop.shopId, barcode })).unwrap();
            handleAddToCart(product);
            setLastScanned(product);
            setSearchInput('');
            dispatch(clearSearch());
        } catch (err) {
            toast.error(typeof err === 'string' ? err : t('catalog.barcodeNotFound'));
        }
    }, [dispatch, selectedShop, handleAddToCart, t]);

    useEffect(() => {
        if (selectedShop) {
            dispatch(fetchCatalog({ shopId: selectedShop.shopId }));
            dispatch(fetchCategories({ shopId: selectedShop.shopId }));
            fetchHeldCarts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, selectedShop]);

    useEffect(() => {
        if (!selectedShop) return;
        const timer = setTimeout(() => {
            if (searchInput.trim().length >= 2) {
                dispatch(setSearchQuery(searchInput));
                dispatch(searchProducts({ shopId: selectedShop.shopId, query: searchInput, limit: 20 }));
            } else if (searchInput.trim().length === 0) {
                dispatch(clearSearch());
            }
        }, 300);
        return () => clearTimeout(timer);
    }, [searchInput, selectedShop, dispatch]);

    const fetchHeldCarts = async () => {
        if (!selectedShop) return;
        try {
            const response = await api.get(API_ENDPOINTS.HELD_CARTS(selectedShop.shopId));
            dispatch(setHeldCartCount(response.data.data.total || 0));
        } catch (err) {
            console.error(getErrorMessage(err));
        }
    };

    const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && /^\d{8,13}$/.test(searchInput.trim())) {
            e.preventDefault();
            handleBarcode(searchInput.trim());
        }
    };

    const displayProducts = searchQuery ? searchResults : selectedCategory === null ? products : products.filter((p) => p.categoryId === selectedCategory);
    const canScan = hasBarcodeDetector();

    if (!selectedShop) return null;

    const scanButton = (
        <IconButton variant="brand" size="lg" label={t('catalog.scanBarcode')} onClick={() => setShowScanner(true)} className="!h-12 !w-12 lg:!h-[50px] lg:!w-[50px]">
            <ScanBarcode />
        </IconButton>
    );

    return (
        <div className="flex h-full min-h-0 flex-1">
            <div className="flex min-w-0 flex-1 flex-col px-5 pt-2 lg:px-6 lg:pt-[22px]">
                <StoreHeader>
                    <div className="hidden min-w-0 flex-1 items-center gap-3 lg:flex">
                        <SearchPill size="lg" value={searchInput} onChange={setSearchInput} onKeyDown={onSearchKeyDown} onClear={() => dispatch(clearSearch())} placeholder={t('catalog.searchOrScan')} />
                        {canScan && scanButton}
                    </div>
                </StoreHeader>

                <div className="mt-3 flex items-center gap-2.5 lg:hidden">
                    <SearchPill size="md" value={searchInput} onChange={setSearchInput} onKeyDown={onSearchKeyDown} onClear={() => dispatch(clearSearch())} placeholder={t('catalog.search')} />
                    {canScan && scanButton}
                </div>

                {!searchQuery && categories.length > 0 && (
                    <div className="no-scrollbar -mx-5 mt-3.5 flex shrink-0 gap-2 overflow-x-auto px-5 pb-0.5 lg:mx-0 lg:mt-4 lg:flex-wrap lg:px-0">
                        <Chip selected={selectedCategory === null} onClick={() => dispatch(setSelectedCategory(null))}>{t('catalog.allProducts')}</Chip>
                        {categories.map((c) => (
                            <Chip key={c.id} selected={selectedCategory === c.id} onClick={() => dispatch(setSelectedCategory(c.id))}>{c.name}</Chip>
                        ))}
                    </div>
                )}

                {!isLandscape && heldCartCount > 0 && (
                    <button type="button" onClick={() => navigate('/held-carts')} className="mt-3.5 flex items-center gap-2.5 rounded-md bg-brandsoft px-3.5 py-2.5 text-[13px] font-bold text-ink-brand">
                        <CirclePause className="h-[18px] w-[18px]" />
                        <span className="flex-1 text-left">{t('cart.heldNotice', { count: heldCartCount })}</span>
                        <span className="font-extrabold text-ink-accent">{t('common.view')}</span>
                    </button>
                )}

                <div className="mt-3.5 min-h-0 flex-1 overflow-y-auto pb-32 lg:mt-4 lg:pb-6">
                    {error && <Alert variant="error" message={error} className="mb-3" />}

                    {isLoading && (
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-3.5">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="overflow-hidden rounded-lg border border-line-subtle bg-surface-card">
                                    <LoadingSkeleton variant="rectangular" height={92} className="!rounded-none" />
                                    <div className="flex flex-col gap-2 p-3"><LoadingSkeleton /><LoadingSkeleton width="60%" /></div>
                                </div>
                            ))}
                        </div>
                    )}

                    {!isLoading && displayProducts.length > 0 && (
                        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 lg:gap-3.5">
                            {displayProducts.map((product) => (
                                <ProductTile key={product.id} product={product} qtyInCart={qtyFor(product)} onClick={handleAddToCart} />
                            ))}
                        </div>
                    )}

                    {!isLoading && displayProducts.length === 0 && (
                        <div className="py-12 text-center">
                            <p className="mb-2 text-ink-muted">{searchQuery ? t('catalog.noResults') : t('catalog.noProducts')}</p>
                            {searchQuery && (
                                <button type="button" onClick={() => { setSearchInput(''); dispatch(clearSearch()); }} className="font-extrabold text-ink-accent">
                                    {t('catalog.clearSearch')}
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {isLandscape ? <CartPanel className="flex" /> : <><CartSync /><FloatingCartBar /></>}

            <BarcodeScannerModal isOpen={showScanner} onClose={() => { setShowScanner(false); setLastScanned(null); }} onDetected={handleBarcode} lastScanned={lastScanned} />
            <VariantPickerModal product={variantParent} onClose={() => setVariantParent(null)} onPick={(v, qty) => { setVariantParent(null); handleAddToCart(v, qty); }} />
        </div>
    );
};

export default CatalogPage;
